import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { apiFetch, apiFetchEmpty } from './client';
import { keys } from './keys';

const LaborRateRowSchema = z.object({
  id: z.coerce.number().optional(),
  community_id: z.coerce.number().optional(),
  item_name: z.string(),
  unit_cost: z.union([z.coerce.number(), z.null()]).optional(),
  effective_date: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
  phase: z.string().nullable().optional(),
  category: z.string().nullable().optional(),
});

const LaborRateHistoryEntrySchema = z.object({
  id: z.coerce.number(),
  unit_cost: z.coerce.number(),
  effective_date: z.string(),
  updated_at: z.string().optional(),
});

export type LaborRateRow = z.infer<typeof LaborRateRowSchema>;
export type LaborRateHistoryEntry = z.infer<typeof LaborRateHistoryEntrySchema>;

export function useLaborRates(communityId: string | undefined) {
  return useQuery({
    queryKey: ['communities', communityId, 'labor-rates'],
    enabled: !!communityId,
    queryFn: () =>
      apiFetch(`/api/communities/${communityId}/labor-rates`, z.array(LaborRateRowSchema.passthrough())),
  });
}

export function useLaborRateHistory(communityId: string | undefined, itemName: string | null) {
  return useQuery({
    queryKey: ['communities', communityId, 'labor-rates', itemName, 'history'],
    enabled: !!communityId && !!itemName,
    queryFn: async () => {
      const data = await apiFetch(
        `/api/communities/${communityId}/labor-rates/${encodeURIComponent(itemName ?? '')}/history`,
        z.object({
          item_name: z.string(),
          history: z.array(LaborRateHistoryEntrySchema),
        })
      );
      return data.history;
    },
  });
}

export type AddLaborRatePayload = {
  item_name: string;
  unit_cost: number;
  effective_date?: string;
  phase?: string | null;
  category?: string | null;
};

export function useAddLaborRate(communityId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AddLaborRatePayload) =>
      apiFetchEmpty(`/api/communities/${communityId}/labor-rates/price`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['communities', communityId, 'labor-rates'] });
      void qc.invalidateQueries({
        queryKey: ['communities', communityId, 'labor-rates', vars.item_name, 'history'],
      });
      void qc.invalidateQueries({ queryKey: keys.communityUnpriced(communityId ?? '') });
      void qc.invalidateQueries({ queryKey: keys.laborAnalysis(communityId ?? '') });
      void qc.invalidateQueries({ queryKey: ['communities', communityId ?? '', 'bid'] });
    },
  });
}

/** @deprecated Bulk save — prefer useAddLaborRate per row. */
export function useSaveLaborRates(communityId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      rates: {
        item_name: string;
        unit_cost: number | null;
        effective_date?: string | null;
        phase?: string | null;
        category?: string | null;
      }[]
    ) =>
      apiFetchEmpty(`/api/communities/${communityId}/labor-rates`, {
        method: 'POST',
        body: JSON.stringify({ rates }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['communities', communityId, 'labor-rates'] });
      void qc.invalidateQueries({ queryKey: keys.communityUnpriced(communityId ?? '') });
      void qc.invalidateQueries({ queryKey: keys.laborAnalysis(communityId ?? '') });
      void qc.invalidateQueries({ queryKey: ['communities', communityId ?? '', 'bid'] });
    },
  });
}
